import { queryOptions, useQuery } from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import ExportService from '~/services/export.service';

import { type PdfDownloadType } from './availablePdfTypes';
import { queryKeysDeliveryNote } from './queryKeys';

type DeliveryNotePdfParams = {
  readonly deliveryNoteId: UUID;
  readonly pdfType: PdfDownloadType;
};

/**
 * Builds query options for requesting, polling, and loading one delivery-note PDF export.
 *
 * @param deliveryNoteId - Delivery note asset ID.
 * @param pdfType - Export format to request.
 * @see https://app.dev.vestigas.com/redoc#tag/Asset/operation/request_pdf_asset__asset_id__request_pdf_get
 * @see https://app.dev.vestigas.com/redoc#tag/Asset/operation/get_pdf_asset__asset_id__get_pdf_get
 */
// fallow-ignore-next-line unused-export -- Canonical query-options API for prefetching and tests.
export function getDeliveryNotePdfQueryOptions({
  deliveryNoteId,
  pdfType,
}: DeliveryNotePdfParams) {
  return queryOptions({
    queryFn: async (): Promise<Blob> => {
      const file = await ExportService.exportSpecificDeliveryNoteAsPDF(
        deliveryNoteId,
        {
          doctype: pdfType,
          includeAttachments: true,
        },
      );

      return file as Blob;
    },
    queryKey: queryKeysDeliveryNote.getPdfPreview(deliveryNoteId, pdfType),
    staleTime: Infinity,
  });
}

/**
 * Loads one generated delivery-note PDF and caches it by delivery note and export type.
 */
export const useQueryDeliveryNotePdf = (params: DeliveryNotePdfParams) =>
  useQuery(getDeliveryNotePdfQueryOptions(params));
